import { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { compose } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';

import { getMessage } from '@gef-ui/localization/messages';

import { codelistProvider, ComponentLoader } from 'components';
import PaginationRow from 'components/atoms/GefMui/PaginationRow';
import TableHasNoResults from 'components/atoms/GefMui/TableHasNoResults';
import { codelists } from 'constants/codelists';
import fetch from 'features/common/fetch';
import { loadMore, paginationClick } from 'features/views/taskStatistics/actions';
import {
	SEARCH_TYPE,
	statusHeaders,
	statusKeys,
	successHeaders,
	successKeys,
} from 'features/views/taskStatistics/constants';
import { getTasksPerUser, getType } from 'features/views/taskStatistics/selectors';

import TaskTableRow from './TaskTableRow';

export const ResultsTable = ({ taskTypesCodelist }) => {
	const dispatch = useDispatch();

	const statsType = useSelector(getType);
	const tasksPerUser = useSelector(getTasksPerUser);

	const handleLoadMore = useCallback(() => {
		dispatch(loadMore());
	}, [dispatch]);

	const handlePaginationClick = useCallback(
		(e, page) => {
			dispatch(paginationClick(page - 1));
		},
		[dispatch],
	);

	const { headers, keys } = useMemo(
		() =>
			statsType === SEARCH_TYPE.success
				? {
						headers: successHeaders,
						keys: successKeys,
				  }
				: {
						headers: statusHeaders,
						keys: statusKeys,
				  },

		[statsType],
	);

	if (!statsType) return null;

	return (
		<ComponentLoader fetchStorePath={fetch.constants.urls.taskStatistics}>
			<TableContainer>
				<Table size="small">
					<TableHead>
						<TableRow>
							{headers.map((header, i) => (
								<TableCell align={i > 1 ? 'center' : 'default'} key={i}>
									{getMessage(header)}
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody sx={{ borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}>
						{tasksPerUser.length === 0 ? (
							<TableHasNoResults colSpan={headers.length} />
						) : (
							tasksPerUser.map((personTasks, userIndex) => (
								<TaskTableRow
									personTasks={personTasks}
									userIndex={userIndex}
									keys={keys}
									taskTypesCodelist={taskTypesCodelist}
								/>
							))
						)}
					</TableBody>
					<PaginationRow
						fetchStorePath={fetch.constants.urls.taskStatistics}
						colSpan={headers.length}
						onLoadNext={handleLoadMore}
						onPaginationClick={handlePaginationClick}
					/>
				</Table>
			</TableContainer>
		</ComponentLoader>
	);
};

ResultsTable.propTypes = {
	taskTypesCodelist: PropTypes.array,
};

export default compose(codelistProvider(codelists.taskTypes, 'taskTypesCodelist'))(ResultsTable);
